import "./CodeEditor.scss";

import { history } from "@codemirror/commands";
import { javascript } from "@codemirror/lang-javascript";
import { json, jsonLanguage, jsonParseLinter } from "@codemirror/lang-json";
import { diagnosticCount, linter } from "@codemirror/lint";
import { Compartment } from "@codemirror/state";
import type { ViewUpdate } from "@codemirror/view";
import { EditorView } from "@codemirror/view";
import CodeMirror from "@uiw/react-codemirror";
import {
  handleRefresh,
  jsonCompletion,
  jsonSchemaLinter,
  stateExtensions,
} from "codemirror-json-schema";
import type { JSONSchema7 } from "json-schema";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useForceUpdate } from "../../hooks/useForceUpdate";
import { useEditorStore } from "./editorStore";
import { HistoryControls } from "./HistoryControls";
import { validateJSON } from "./jsonValidator";

interface CodeEditorProps {
  placeholder?: string;
  initialValue?: Object | string;
  lineNumbers?: boolean;
  withToolbar?: boolean;
  minHeight?: string;
  onChange?: (value: string) => void;
  setIsError?: (value: boolean) => void;
  readOnly?: boolean;
  autofocus?: boolean;
  withFooter?: boolean;
  validationSchema?: JSONSchema7;
  onError?: (error: boolean) => void;
  withType?: boolean;
  setEditorReady?: (value: boolean) => void;
  ref?: any;
  inPopup?: boolean;
  withBanner?: boolean;
}

export const CodeEditor: React.FC<CodeEditorProps> = forwardRef(
  (
    {
      placeholder,
      lineNumbers = true,
      withToolbar,
      minHeight,
      initialValue,
      onChange,
      setIsError,
      readOnly,
      autofocus,
      withFooter,
      validationSchema,
      onError,
      withType,
      setEditorReady,
      inPopup,
      withBanner,
    },
    forwardRef,
  ) => {
    const {
      code,
      setCode,
      isJavaScriptMode,
      setValidationError,
      needValidation,
      setNeedValidation,
    } = useEditorStore();
    const forceUpdate = useForceUpdate();

    const viewRef = useRef<EditorView>();
    let undoRedo = useMemo(() => new Compartment(), []);

    const [isCreatedEditor, setIsCreatedEditor] = useState(false);

    useEffect(() => {
      return () => {
        setEditorReady && setEditorReady(false);
      };
    }, []);

    const clearHistory = useCallback(() => {
      if (viewRef.current) {
        viewRef.current.dispatch({
          effects: undoRedo.reconfigure([]),
        });
        viewRef.current.dispatch({
          effects: undoRedo.reconfigure([history()]),
        });
        forceUpdate();
      }
    }, [forceUpdate, undoRedo]);

    useImperativeHandle(
      forwardRef,
      () => {
        return viewRef.current;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isCreatedEditor],
    );

    useEffect(() => {
      switch (typeof initialValue) {
        case "string":
          setCode(initialValue);
          break;
        case "object":
          setCode(JSON.stringify(initialValue, null, 2));
          break;
        default:
          setCode(undefined);
      }
      setTimeout(() => {
        clearHistory();
      });
    }, [initialValue, clearHistory, setCode]);

    const handleChange = (value: string) => {
      setCode(value);
      if (!isJavaScriptMode) {
        const error = validateJSON(value);
        setValidationError(error);
        setIsError && setIsError(!!error);
        onChange && onChange(value);
      } else {
        setValidationError(null);
      }
    };

    const handleEditorUpdate = useCallback((update: ViewUpdate) => {
      setNeedValidation(!!update.state.toJSON().doc);
      if (onError) {
        const errorsCount = diagnosticCount(update.state);
        onError(errorsCount > 0 || !update.state.toJSON().doc);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const extensions = useMemo(() => {
      const baseExts: any = [EditorView.lineWrapping, undoRedo.of([history()])];

      let extraExt = [
        linter(jsonParseLinter(), {
          delay: 300,
        }),
        json(),
        // isJavaScriptMode ? javascript() : json(),
      ];
      if (validationSchema) {
        extraExt = [
          ...extraExt,
          linter(jsonSchemaLinter(), {
            needsRefresh: handleRefresh,
          }),
          jsonLanguage.data.of({
            autocomplete: jsonCompletion(),
          }),
          stateExtensions(validationSchema),
          jsonLanguage.data.of({
            autocomplete: jsonCompletion(),
          }),
        ];
      }
      return needValidation ? baseExts.concat(extraExt) : baseExts;
    }, [needValidation, validationSchema, undoRedo]);

    const basicSetup = useMemo(() => {
      return {
        lineNumbers: lineNumbers,
        foldGutter: lineNumbers,
        highlightActiveLineGutter: false,
        history: false,
        lineWrapping: false,
        highlightActiveLine: false,
      };
    }, [lineNumbers]);

    const handleCrateEditor = useCallback(
      (view: EditorView) => {
        viewRef.current = view;
        setTimeout(() => {
          setIsCreatedEditor(true);
          setEditorReady && setEditorReady(true);
        });
      },
      [setEditorReady],
    );

    return (
      <div className="d-flex flex-column flex-grow-1">
        {withToolbar && (
          <div
            className={`d-flex justify-content-between align-items-center history-controls-container ${
              inPopup ? "in-popup" : ""
            } ${withBanner ? "with-banner" : ""}`}
          >
            <HistoryControls viewRef={viewRef} />
          </div>
        )}
        <div className="code-editor-wrapper">
          {/* placeholder to avoid scroll jumping */}
          {!isCreatedEditor && (
            <pre
              className="code-editor-placeholder py-7"
              style={{
                minHeight: minHeight,
              }}
            >
              {typeof initialValue === "string"
                ? initialValue
                : JSON.stringify(initialValue, null, 2)}
            </pre>
          )}

          <CodeMirror
            readOnly={readOnly}
            className={`code-editor ${withFooter ? "with-footer" : ""} ${
              isCreatedEditor ? "" : "d-none"
            } ${withType ? "with-type" : ""}`}
            value={code || undefined}
            minHeight={minHeight}
            extensions={extensions}
            onChange={handleChange}
            placeholder={placeholder}
            onCreateEditor={handleCrateEditor}
            basicSetup={basicSetup}
            autoFocus={autofocus}
            onUpdate={handleEditorUpdate}
          />
        </div>
        {/* {validationError && <ErrorMessage error={validationError} />} */}
      </div>
    );
  },
);
